// Client-side upload validation for the Paint Studio.
// Runs before we upload to storage so bad files never reach the AI budget.

export const UPLOAD_MAX_BYTES = 15 * 1024 * 1024; // 15 MB
export const UPLOAD_MIN_DIM = 480;
export const UPLOAD_MAX_DIM = 4096; // target for AI detection; larger images are auto-downscaled
export const UPLOAD_HARD_MAX_BYTES = 40 * 1024 * 1024; // 40 MB — anything bigger is rejected outright
export const UPLOAD_ALLOWED_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
] as const;

export type UploadCheck =
  | { ok: true; file: File; width: number; height: number }
  | { ok: false; reason: string };

function guessMimeFromName(name: string): string | null {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  return null;
}

export async function validateUpload(file: File): Promise<UploadCheck> {
  if (!file) return { ok: false, reason: "No file selected." };
  // Some browsers/OSes report an empty MIME (drag-drop, share sheet). Fall back to the extension.
  const mime = file.type || guessMimeFromName(file.name) || "";
  if (!(UPLOAD_ALLOWED_MIME as readonly string[]).includes(mime)) {
    return { ok: false, reason: "Please upload a JPG, PNG, or WEBP photo. HEIC files must be converted first." };
  }
  if (file.size > UPLOAD_HARD_MAX_BYTES) {
    return { ok: false, reason: `Photo is ${(file.size / 1024 / 1024).toFixed(1)} MB — please keep it under ${Math.round(UPLOAD_HARD_MAX_BYTES / 1024 / 1024)} MB.` };
  }
  const dims = await readDimensions(file);
  if (!dims) return { ok: false, reason: "Couldn't read this image. Try a different file." };
  if (dims.width < UPLOAD_MIN_DIM || dims.height < UPLOAD_MIN_DIM) {
    return { ok: false, reason: `Photo is too small (${dims.width}×${dims.height}). Please use at least ${UPLOAD_MIN_DIM}px on the short side.` };
  }
  // Auto-downscale oversized images (modern phone cameras easily exceed 6000px)
  // and re-compress big files so the storage upload doesn't stall.
  const needsResize = dims.width > UPLOAD_MAX_DIM || dims.height > UPLOAD_MAX_DIM;
  const needsRecompress = file.size > UPLOAD_MAX_BYTES;
  if (needsResize || needsRecompress) {
    try {
      const out = await downscaleImage(file, UPLOAD_MAX_DIM);
      return { ok: true, file: out.file, width: out.width, height: out.height };
    } catch {
      return { ok: false, reason: "Couldn't resize this photo. Try a smaller file." };
    }
  }
  return { ok: true, file, width: dims.width, height: dims.height };
}

function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

async function downscaleImage(file: File, maxDim: number): Promise<{ file: File; width: number; height: number }> {
  const bmp = await createImageBitmap(file).catch(() => null);
  let width: number, height: number;
  let source: CanvasImageSource;
  if (bmp) {
    width = bmp.width; height = bmp.height; source = bmp;
  } else {
    // Fallback for browsers without createImageBitmap on this file type
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    URL.revokeObjectURL(url);
    width = img.naturalWidth; height = img.naturalHeight; source = img;
  }
  const scale = Math.min(1, maxDim / Math.max(width, height));
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(source, 0, 0, w, h);
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/jpeg", 0.9),
  );
  const base = file.name.replace(/\.[^.]+$/, "") || "photo";
  const out = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  return { file: out, width: w, height: h };
}
